import { useMutation } from "@apollo/client/react";
import {
  CreateStudyProgramme,
  CreateStudyProgrammeVariables,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { Section } from "common/components/Section";
import { markdownInput } from "common/input-types";
import gql from "graphql-tag";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";

const MUTATION = gql`
  mutation CreateStudyProgramme($input: CreateStudyProgrammeInput!) {
    createStudyProgramme(input: $input) {
      studyProgramme {
        id
      }
    }
  }
`;

interface Props {
  cancel(): void;
  complete(): void;
}

interface FormValues {
  title: string;
  description: string;
}

export const StudyProgrammeCreate: React.FC<React.PropsWithChildren<Props>> = ({
  cancel,
  complete,
}) => {
  const methods = useForm<FormValues>();

  const [mutate, { error, loading }] = useMutation<
    CreateStudyProgramme,
    CreateStudyProgrammeVariables
  >(MUTATION);

  const onSubmit = async (input: FormValues) => {
    const result = await mutate({
      variables: {
        input: {
          title: input.title,
          description: markdownInput(input.description),
        },
      },
    });

    if (result.data) {
      complete();
    }
  };

  return (
    <Section title="Nytt studieprogram">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <TextField
            title="Tittel"
            fieldProps={methods.register("title", {
              required: "Du må gi studieprogrammet en tittel",
            })}
          />

          <MarkdownField<FormValues>
            name="description"
            title="Beskrivelse av studieprogram"
            defaultValue=""
          />

          <MutationLoadingOrError loading={loading} error={error} />

          <CancelOrSubmit onCancel={cancel} isLoading={loading} />
        </form>
      </FormProvider>
    </Section>
  );
};
